import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CheckCircle, Buildings } from "@phosphor-icons/react";

const inputCls = "w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

export default function IntakeForm() {
  const { token } = useParams();
  const { data: info, isError } = useQuery({ queryKey: ["intake", token], queryFn: () => api.publicIntakeInfo(token), retry: false });
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", phone: "", hotel_id: "", check_in: "", check_out: "", num_rooms: "", meeting_needs: "", notes: "" });
  const [done, setDone] = useState(false);
  const S = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = useMutation({
    mutationFn: () => api.publicIntakeSubmit(token, { ...f, num_rooms: Number(f.num_rooms) || 0 }),
    onSuccess: () => setDone(true),
    onError: (e) => toast.error(errMsg(e, "Could not submit")),
  });

  if (isError) return <div className="min-h-screen grid place-items-center px-6"><p className="text-[#4B5563]">This link is no longer valid.</p></div>;

  if (done) return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="intake-done">
      <div className="max-w-md text-center">
        <CheckCircle size={56} weight="fill" color="#047857" className="mx-auto" />
        <h1 className="font-display font-black text-3xl text-[#111827] mt-4">Thank you!</h1>
        <p className="text-[#4B5563] mt-2">Your stay requirements have been sent to {info?.org_name || "the hotel team"}. A sales representative will follow up shortly.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-10 px-5" data-testid="intake-form">
      <div className="max-w-lg mx-auto bg-white border border-[#111827]">
        <div className="bg-[#002FA7] text-white px-6 py-4 flex items-center gap-3">
          <Buildings size={22} weight="bold" />
          <div className="leading-tight">
            <div className="font-display font-bold text-lg">{info?.hotel?.name || "RateBridge"}</div>
            {info?.hotel && <div className="text-xs text-white/80">{[info.hotel.city, info.hotel.state].filter(Boolean).join(", ")}</div>}
          </div>
        </div>
        {info?.hotel?.image && (
          <img src={info.hotel.image} alt={info.hotel.name} className="w-full h-40 object-cover" data-testid="intake-hotel-image" />
        )}
        <div className="p-6">
          <span className="overline text-[#002FA7]">Stay requirements</span>
          <h1 className="font-display font-black text-2xl text-[#111827] mt-1" data-testid="intake-title">Tell {info?.hotel?.name || info?.org_name || "us"} about your group</h1>
          {info?.hotel?.address && <p className="text-sm text-[#4B5563] mt-1">{info.hotel.address}</p>}
          <p className="text-sm text-[#4B5563] mt-1">{info?.account_name ? `${info.account_name} · ` : ""}Fill this out and our sales team will send you a proposal.</p>

          <form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="mt-5 space-y-3">
            <input data-testid="intake-company" required value={f.company_name} onChange={S("company_name")} placeholder="Company / group name *" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="intake-contact" value={f.contact_name} onChange={S("contact_name")} placeholder="Your name" className={inputCls} />
              <input data-testid="intake-phone" value={f.phone} onChange={S("phone")} placeholder="Phone" className={inputCls} />
            </div>
            <input data-testid="intake-email" type="email" value={f.email} onChange={S("email")} placeholder="Email" className={inputCls} />
            {!info?.fixed_hotel_id && (info?.properties?.length > 0) && (
              <select data-testid="intake-hotel" value={f.hotel_id} onChange={S("hotel_id")} className={`${inputCls} bg-white`}>
                <option value="">Preferred hotel (optional)</option>
                {info.properties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.city ? ` · ${p.city}` : ""}</option>)}
              </select>
            )}
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-[#4B5563]">Check-in<input data-testid="intake-checkin" type="date" value={f.check_in} onChange={S("check_in")} className={inputCls} /></label>
              <label className="text-xs text-[#4B5563]">Check-out<input data-testid="intake-checkout" type="date" value={f.check_out} onChange={S("check_out")} className={inputCls} /></label>
              <label className="text-xs text-[#4B5563]"># Rooms<input data-testid="intake-rooms" type="number" value={f.num_rooms} onChange={S("num_rooms")} className={inputCls} /></label>
            </div>
            <textarea data-testid="intake-meeting" rows={2} value={f.meeting_needs} onChange={S("meeting_needs")} placeholder="Meeting / event space needs (optional)" className={inputCls} />
            <textarea data-testid="intake-notes" rows={2} value={f.notes} onChange={S("notes")} placeholder="Anything else we should know?" className={inputCls} />
            <button data-testid="intake-submit" type="submit" disabled={submit.isPending || !f.company_name.trim()}
              className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.99] disabled:opacity-60">
              {submit.isPending ? "Sending…" : "Send my requirements"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
